import {
  AppModal,
  Button,
  Input,
  LoadingSpinner,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import {
  FileRepositorySort,
  type FileSortKey,
} from "@/components/features/matter/file-repo/FileRepositorySort";
import { retrieveFirmFilesOptions } from "@/lib/data/react-query/firm/firm-file";
import type { FirmFile } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  buildFirmFileBreadcrumbs,
  getFirmFileDisplayName,
  getFirmFileExtension,
  sortFirmFiles,
} from "@/lib/utils/firmFile";
import { getFileIcon } from "@/lib/utils/matterFile";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Folder, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FirmFolderTree } from "./FirmFolderTree";

interface SelectFirmFileModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onFirmFilesSelected: (files: FirmFile[]) => Promise<void> | void;
  enabledFileTypes?: string[] | undefined;
  multiple?: boolean;
  maxSelection?: number;
  /** When opening, start browsing in this folder (e.g. firm "Templates"). */
  initialFolderId?: string;
  title?: string;
  description?: string;
}

export const SelectFirmFileModal = ({
  open,
  setOpen,
  onFirmFilesSelected,
  enabledFileTypes,
  multiple = false,
  maxSelection,
  initialFolderId,
  title = "Select firm file",
  description = "Choose a firm file",
}: SelectFirmFileModalProps) => {
  const [selectedFiles, setSelectedFiles] = useState<FirmFile[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [isConfirmingSelection, setIsConfirmingSelection] = useState(false);
  const [sortKey, setSortKey] = useState<FileSortKey>("dateModified");
  const [currentFolderId, setCurrentFolderId] = useState("");
  const { data, isLoading } = useQuery(retrieveFirmFilesOptions(open));
  const firmFiles = useMemo(() => data?.data ?? [], [data?.data]);

  const allowedFileTypes = useMemo(() => {
    if (!enabledFileTypes || enabledFileTypes.length === 0) return null;
    return new Set(enabledFileTypes.map((type) => type.toLowerCase()));
  }, [enabledFileTypes]);

  const breadcrumbs = useMemo(
    () => buildFirmFileBreadcrumbs(firmFiles, currentFolderId),
    [currentFolderId, firmFiles],
  );

  const currentItems = useMemo(
    () =>
      firmFiles.filter((file) => (file.idLocation || "") === currentFolderId),
    [currentFolderId, firmFiles],
  );

  const filteredItems = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    return currentItems.filter((file) => {
      if (file.isFolder) {
        return !search || file.name.toLowerCase().includes(search);
      }

      const extension = getFirmFileExtension(file);
      if (allowedFileTypes && !allowedFileTypes.has(extension.toLowerCase())) {
        return false;
      }

      if (search) {
        return getFirmFileDisplayName(file).toLowerCase().includes(search);
      }

      return true;
    });
  }, [allowedFileTypes, currentItems, searchValue]);

  const sortedItems = useMemo(() => {
    const folders = filteredItems
      .filter((file) => file.isFolder)
      .sort((a, b) => a.name.localeCompare(b.name));
    const files = sortFirmFiles(
      filteredItems.filter((file) => !file.isFolder),
      sortKey,
    );
    return [...folders, ...files];
  }, [filteredItems, sortKey]);

  const isSelectionMaxed =
    typeof maxSelection === "number" && selectedFiles.length >= maxSelection;

  const handleSelectFirmFile = (file: FirmFile) => {
    if (file.isFolder) {
      setCurrentFolderId(file.id);
      return;
    }

    if (multiple) {
      setSelectedFiles((prev) => {
        const isAlreadySelected = prev.some((item) => item.id === file.id);
        if (isAlreadySelected) {
          return prev.filter((item) => item.id !== file.id);
        }
        if (isSelectionMaxed) {
          return prev;
        }
        return [...prev, file];
      });
    } else {
      setSelectedFiles([file]);
    }
  };

  const handleRemoveSelectedFile = (fileId: string) => {
    setSelectedFiles((prev) => prev.filter((item) => item.id !== fileId));
  };

  const handleConfirmSelection = async () => {
    if (selectedFiles.length === 0 || isConfirmingSelection) return;
    setIsConfirmingSelection(true);
    try {
      await onFirmFilesSelected(selectedFiles);
      setSelectedFiles([]);
      setOpen(false);
    } catch (error) {
      console.error("Unable to add firm files", error);
    } finally {
      setIsConfirmingSelection(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setSelectedFiles([]);
      setSearchValue("");
      setCurrentFolderId("");
    } else {
      setCurrentFolderId(initialFolderId ?? "");
    }
  }, [open, initialFolderId]);

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      size="4xl"
      overlayClassName="z-[102] bg-black/45 backdrop-blur-md"
      className="z-[103]"
      title={title}
      description={description}
      isDirty={selectedFiles.length > 0}
      footer={
        <div className="flex w-full justify-end gap-2">
          <FormCancelButton
            setOpen={setOpen}
            isDirty={selectedFiles.length > 0}
          />
          <Button
            type="button"
            onClick={handleConfirmSelection}
            disabled={selectedFiles.length === 0 || isConfirmingSelection}
          >
            {isConfirmingSelection && (
              <LoadingSpinner className="mr-2 h-4 w-4" />
            )}
            Select firm file{multiple && "s"}
          </Button>
        </div>
      }
    >
      {isConfirmingSelection && <FormLoadingOverlay />}
      <div className="flex grow overflow-hidden">
        <div className="border-border w-72 shrink-0 border-r p-4">
          <FirmFolderTree
            files={firmFiles}
            currentFolderId={currentFolderId}
            onNavigateToFolder={setCurrentFolderId}
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <div className="overflow-y-auto">
            <div className="space-y-4 p-4">
              <div>
                <Typography className="mb-2 text-sm font-medium">
                  Selected firm file{multiple && "s"} ({selectedFiles.length}
                  {maxSelection && ` / ${maxSelection}`})
                </Typography>
                {selectedFiles.length > 0 ? (
                  <div className="space-y-2">
                    {selectedFiles.map((file) => {
                      const FileIcon = getFileIcon(
                        getFirmFileDisplayName(file),
                      );
                      return (
                        <div
                          key={file.id}
                          className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                        >
                          <div className="flex min-w-0 items-center gap-x-2">
                            <FileIcon className="h-4 w-4 text-blue-500" />
                            <span className="truncate">
                              {getFirmFileDisplayName(file)}
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              handleRemoveSelectedFile(file.id);
                            }}
                            className="ml-2"
                            aria-label={`Remove ${getFirmFileDisplayName(file)}`}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Typography className="text-muted-foreground text-sm">
                    No firm file{multiple && "s"} selected
                  </Typography>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-sm">
                <button
                  type="button"
                  className="text-primary"
                  onClick={() => {
                    setCurrentFolderId("");
                  }}
                >
                  Firm files
                </button>
                {breadcrumbs.map((folder) => (
                  <div key={folder.id} className="flex items-center gap-2">
                    <ChevronRight className="text-muted-foreground h-4 w-4" />
                    <button
                      type="button"
                      className="text-primary"
                      onClick={() => {
                        setCurrentFolderId(folder.id);
                      }}
                    >
                      {folder.name}
                    </button>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <Input
                  placeholder="Search this folder..."
                  value={searchValue}
                  onChange={(event) => {
                    setSearchValue(event.target.value);
                  }}
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Typography className="text-sm font-medium">
                    Firm files
                  </Typography>
                  <FileRepositorySort
                    sortKey={sortKey}
                    onSortChange={(key) => {
                      setSortKey(key);
                    }}
                  />
                </div>
                {isLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <LoadingSpinner />
                  </div>
                ) : sortedItems.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {sortedItems.map((file) => {
                      const isSelected = selectedFiles.some(
                        (item) => item.id === file.id,
                      );
                      const isDisabled =
                        !file.isFolder && isSelectionMaxed && !isSelected;
                      const extension = getFirmFileExtension(file);
                      const displayName = getFirmFileDisplayName(file);
                      const FileIcon = file.isFolder
                        ? Folder
                        : getFileIcon(displayName);

                      return (
                        <button
                          type="button"
                          key={file.id}
                          onClick={() => {
                            handleSelectFirmFile(file);
                          }}
                          disabled={isDisabled}
                          className={cn(
                            "hover:bg-primary/5 group relative flex flex-col gap-3 rounded-md border p-3 text-left text-sm transition-colors",
                            !file.isFolder &&
                              isSelected &&
                              "bg-primary/10 border-primary/30 hover:bg-primary/20",
                            isDisabled && "cursor-not-allowed opacity-60",
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <FileIcon className="h-6 w-6 text-blue-500" />
                            {file.isFolder ? (
                              <span className="text-muted-foreground text-xs">
                                Open folder
                              </span>
                            ) : (
                              multiple && (
                                <span className="text-muted-foreground text-xs">
                                  {isSelected ? "Selected" : ""}
                                </span>
                              )
                            )}
                          </div>
                          <div className="min-w-0">
                            <span className="block truncate font-medium">
                              {displayName}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              {file.isFolder
                                ? "Folder"
                                : `${(extension || "file").toUpperCase()} · ${new Date(file.dateModified).toLocaleDateString()}`}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <Typography className="text-muted-foreground text-sm">
                    No firm files found.
                  </Typography>
                )}
              </div>
            </div>
          </div>
          <div className="flex grow" />
        </div>
      </div>
    </AppModal>
  );
};
